import zoomSdk from "@zoom/appssdk";
import { 
  SUMMARIZE_STATES,
  RTMS_REQUESTER_DEFAULT_NAME, 
  START_DISCLAIMER_CHAT_MESSAGE,
} from "../../constants";
import LogManager from "../../utils/logManager";
import { fetchRtmsSessionStatus, SESSION_STATUS, SESSION_STATUS_MESSAGE } from "../../utils/rtmsSessionStatusUtils";
import { fetchRtmsStatusUpdate } from "../../utils/rtmsStatusUpdateUtils";
import { fetchSummaries } from "../../utils/summaryUtils";

/**
 * Start RTMS summarization session.
 * 
 * This function handles the complete flow of starting an RTMS session:
 * 1. Calls the backend API to request RTMS start
 * 2. Polls for session status to confirm activation
 * 3. Handles timeout and error scenarios
 * 4. Manages rejection when another user has already started a session
 * 
 * @param {object} params - Parameters for starting summarization
 * @param {string} params.summarizeState - Current summarize state
 * @param {function} params.setSummarizeState - State setter for summarize state
 * @param {function} params.setSummarizeClickCount - State setter for click count
 * @param {function} params.setRtmsRequesterName - State setter for requester name
 * @param {function} params.setRtmsStreamId - State setter for stream ID
 * @param {function} params.setRtmsErrorType - State setter for error type
 * @param {function} params.setIsRtmsErrorModalOpen - State setter for error modal
 * @param {function} params.setIsServerErrorModalOpen - State setter for server error modal
 * @param {string} params.meetingUUID - Meeting UUID
 * @param {string} params.meetingId - Meeting ID
 * @param {string} params.role - User's role
 * @param {string} params.appContext - App context for backend validation
 * @param {boolean} params.isRestartAttempt - Whether this is a restart attempt (longer timeout)
 * @param {boolean} params.isAutostart - Transcript auto-start on mount; state is already LOADING
 * @param {object} params.userInitiatedStartRef - Set while this user's start is in flight (per-host mode)
 * @param {object} params.sessionStatusIntervalRef - Ref for polling interval
 * @param {object} params.sessionStatusTimeoutRef - Ref for timeout
 * @param {object} params.rejectionTimeoutRef - Ref for rejection timeout
 */
export const onSummarize = async (params) => {
  const {
    summarizeState,
    setSummarizeState,
    setSummarizeClickCount,
    setRtmsRequesterName,
    setMeetingAgendaActive,
    setMeetingActionItemsActive,
    setRtmsStreamId,
    setRtmsErrorType,
    setIsRtmsErrorModalOpen,
    setIsServerErrorModalOpen,
    meetingUUID,
    meetingId,
    role,
    appContext,
    isRestartAttempt = false,
    isAutostart = false,
    userInitiatedStartRef,
    sessionStatusIntervalRef,
    sessionStatusTimeoutRef,
    rejectionTimeoutRef,
    rtmsStartTimestampRef,
  } = params;

  if (summarizeState === SUMMARIZE_STATES.LOADING && !isAutostart) return;
  setSummarizeState(SUMMARIZE_STATES.LOADING);
  
  if (isRestartAttempt) {
    await new Promise(resolve => setTimeout(resolve, 45000));
  }
  userInitiatedStartRef.current = true;
  
  let rtmsResponse = {};
  try {
    rtmsResponse = await fetchRtmsStatusUpdate("start", meetingUUID, meetingId, role, appContext);
  } catch (e) {
    LogManager.error("Error calling /rtms-status-update:", e);
    rtmsResponse = "Error";
  }

  if (rtmsResponse.result === "OK") {
    setSummarizeClickCount((prev) => prev + 1);
    
    if (sessionStatusIntervalRef.current) clearInterval(sessionStatusIntervalRef.current);
    if (sessionStatusTimeoutRef.current) clearTimeout(sessionStatusTimeoutRef.current);
    
    sessionStatusIntervalRef.current = setInterval(async () => {
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUUID);
        if (sessionStatus.status === SESSION_STATUS.ACTIVE) {
          if (sessionStatus.rtmsStreamId) setRtmsStreamId(sessionStatus.rtmsStreamId);
          setMeetingAgendaActive(sessionStatus.meetingAgendaActive);
          setMeetingActionItemsActive(sessionStatus.meetingActionItemsActive);
          rtmsStartTimestampRef.current = sessionStatus.rtmsStartTime;
          clearInterval(sessionStatusIntervalRef.current);
          clearTimeout(sessionStatusTimeoutRef.current);
          setSummarizeState(SUMMARIZE_STATES.SUMMARIZING);
        } else if (sessionStatus.statusMessage === SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR || 
                   sessionStatus.statusMessage === SESSION_STATUS_MESSAGE.RTMS_RUNTIME_ERROR) {
          clearInterval(sessionStatusIntervalRef.current);
          clearTimeout(sessionStatusTimeoutRef.current);
          userInitiatedStartRef.current = false;
          setSummarizeState(SUMMARIZE_STATES.DEFAULT);
          setRtmsErrorType(sessionStatus.statusMessage);
          setIsRtmsErrorModalOpen(true);
        }
      } catch (error) {
        LogManager.error("Error polling session status:", error);
      }
    }, 5000);
    
    const timeoutDuration = isRestartAttempt ? 60000 : 15000;
    sessionStatusTimeoutRef.current = setTimeout(async () => {
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUUID);
        if (sessionStatus.status !== SESSION_STATUS.ACTIVE) {
          clearInterval(sessionStatusIntervalRef.current);
          userInitiatedStartRef.current = false;
          setSummarizeState(SUMMARIZE_STATES.DEFAULT);
          setRtmsErrorType(SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR);
          setIsRtmsErrorModalOpen(true);
        }
      } catch (error) {
        clearInterval(sessionStatusIntervalRef.current);
        userInitiatedStartRef.current = false;
        setSummarizeState(SUMMARIZE_STATES.DEFAULT);
        setIsServerErrorModalOpen(true);
      }
    }, timeoutDuration);
  } else if (rtmsResponse.result === "REJECTED") {
    userInitiatedStartRef.current = false;
    setSummarizeState(SUMMARIZE_STATES.REJECTED);
    setRtmsRequesterName(rtmsResponse.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
    rejectionTimeoutRef.current = setTimeout(() => {
      setSummarizeState(SUMMARIZE_STATES.DEFAULT);
      setRtmsRequesterName(null);
    }, 120000);
  } else {
    userInitiatedStartRef.current = false;
    setSummarizeState(SUMMARIZE_STATES.DEFAULT);
    setIsServerErrorModalOpen(true);
  }
};

/**
 * Stop RTMS summarization session.
 * 
 * This function handles stopping an active RTMS session by calling
 * the backend API. If the stop request fails, it shows an error modal.
 * 
 * @param {object} params - Parameters for stopping summarization
 * @param {string} params.summarizeState - Current summarize state
 * @param {function} params.setSummarizeState - State setter for summarize state
 * @param {function} params.setIsServerErrorModalOpen - State setter for server error modal
 * @param {string} params.meetingUUID - Meeting UUID
 * @param {string} params.meetingId - Meeting ID
 * @param {string} params.role - User's role
 * @param {string} params.appContext - App context for backend validation
 */
export const onEndSummarize = async (params) => {
  const {
    summarizeState,
    setSummarizeState,
    setIsServerErrorModalOpen,
    meetingUUID,
    meetingId,
    role,
    appContext,
  } = params;

  if (summarizeState === SUMMARIZE_STATES.LOADING) return;
  setSummarizeState(SUMMARIZE_STATES.LOADING);
  
  let rtmsResponse = {};
  try {
    rtmsResponse = await fetchRtmsStatusUpdate("stop", meetingUUID, meetingId, role, appContext);
  } catch (e) {
    LogManager.error("Error stopping RTMS:", e);
    rtmsResponse = "Error";
  }

  if (rtmsResponse.result !== "OK") {
    setSummarizeState(SUMMARIZE_STATES.DEFAULT);
    setIsServerErrorModalOpen(true);
  }
};


/**
 * Create a handler for RTMS status change events from Zoom SDK.
 * 
 * This handler is called when RTMS session status changes (started/stopped).
 * It updates the UI state accordingly and handles error scenarios.
 * 
 * @param {object} params - Parameters for the handler
 * @param {string} params.meetingUUID - Meeting UUID
 * @param {string} params.alias - Current user's alias
 * @param {function} params.setSummarizeState - State setter for summarize state
 * @param {function} params.setIsRtmsStartedByOther - State setter for RTMS started by other
 * @param {function} params.setRtmsRequesterName - State setter for requester name
 * @param {function} params.setRtmsStreamId - State setter for stream ID
 * @param {function} params.setRtmsErrorType - State setter for error type
 * @param {function} params.setIsRtmsErrorModalOpen - State setter for error modal
 * @param {function} params.setAimsUuid - State setter for AIMS UUID
 * @param {function} params.setSummaryData - State setter for summary data
 * @param {object} params.summarizeStateRef - Ref for summarize state
 * @param {object} params.isRtmsStartedByOtherRef - Ref for RTMS started by other
 * @param {object} params.rtmsStartTimestampRef - Ref for RTMS start timestamp
 * @param {object} params.hideStoppedComponentTimerRef - Ref for hide stopped component timer
 * @param {boolean} params.isTranscribe - Transcribe flow: post the start message to chat on own start
 * @param {boolean} params.isPerHost - Per-host transcription (autoStartMode MULTIPLE): every host runs
 *   their own stream, so only react to this user's own start / stop
 * @param {object} params.userInitiatedStartRef - Set while this user's start is in flight
 * @param {object} params.rtmsSessionIdRef - This user's rtmsSessionId, to match "stopped" in per-host mode
 * @returns {function} Event handler function for RTMS status changes
 */
export const createRTMSChangeHandler = (params) => {
  const {
    meetingUUID,
    alias,
    setSummarizeState,
    setIsRtmsStartedByOther,
    setRtmsRequesterName,
    setMeetingAgendaActive,
    setMeetingActionItemsActive,
    setRtmsStreamId,
    setRtmsErrorType,
    setIsRtmsErrorModalOpen,
    setIsProductFeedbackModalOpen,
    setAimsUuid,
    setSummaryData,
    summarizeStateRef,
    isRtmsStartedByOtherRef,
    rtmsStartTimestampRef,
    hideStoppedComponentTimerRef,
    isTranscribe,
    isPerHost,
    userInitiatedStartRef,
    rtmsSessionIdRef,
  } = params;

  // Transcribe flow: post the start message to chat when this user started the session.
  const sendTranscriptionStartToChat = () => {
    if (!isTranscribe) return;
    zoomSdk.sendMessageToChat({ message: START_DISCLAIMER_CHAT_MESSAGE })
      .catch((error) => LogManager.error("Error sending transcription start message:", error));
  };

  return async (event) => {
    LogManager.info("RTMS changed:", JSON.stringify(event));
    
    if (event.status === "started" && isPerHost) {
      if (!userInitiatedStartRef.current) {
        LogManager.info("RTMS started by another host - ignoring in per-host mode");
        return;
      }
      userInitiatedStartRef.current = false;
      rtmsSessionIdRef.current = event.rtmsSessionId || null;
      setIsRtmsStartedByOther(false);
      sendTranscriptionStartToChat();
      setSummarizeState(SUMMARIZE_STATES.SUMMARIZING);
      rtmsStartTimestampRef.current = Math.floor(Date.now() / 1000);
    } else if (event.status === "started") {
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUUID);
        LogManager.info("Session status when started:", sessionStatus);
        
        if (sessionStatus.rtmsStreamId) {
          setRtmsStreamId(sessionStatus.rtmsStreamId);
        }

        const isActiveSession = sessionStatus.status === SESSION_STATUS.ACTIVE;
        setMeetingAgendaActive(isActiveSession && sessionStatus.meetingAgendaActive);
        setMeetingActionItemsActive(isActiveSession && sessionStatus.meetingActionItemsActive);

        if (!sessionStatus.rtmsRequesterAlias) {
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
        } else if (sessionStatus.rtmsRequesterAlias !== alias) {
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
        } else {
          setIsRtmsStartedByOther(false);
          sendTranscriptionStartToChat();
        }
      } catch (error) {
        LogManager.error("Error fetching session status on RTMS start:", error);
        setMeetingAgendaActive(false);
        setMeetingActionItemsActive(false);
        setIsRtmsStartedByOther(true);
        setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
      }
      
      setSummarizeState(SUMMARIZE_STATES.SUMMARIZING);
      rtmsStartTimestampRef.current = Math.floor(Date.now() / 1000);
      
    } else if (event.status === "stopped") {
      if (isPerHost) {
        // Only this user's own session; ignore all stops if we never started one.
        if (!rtmsSessionIdRef.current) return;
        if (event.rtmsSessionId && event.rtmsSessionId !== rtmsSessionIdRef.current) return;
        rtmsSessionIdRef.current = null;
      }
      if (!isRtmsStartedByOtherRef.current) {
        setIsProductFeedbackModalOpen(true);
      }
      setIsRtmsStartedByOther(false);
      setRtmsRequesterName(null);
      setSummarizeState(SUMMARIZE_STATES.STOPPED);
      
      fetchRtmsSessionStatus(meetingUUID).then((sessionData) => {
        if (sessionData.statusMessage === SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR || 
            sessionData.statusMessage === SESSION_STATUS_MESSAGE.RTMS_RUNTIME_ERROR) {
          if (!isRtmsStartedByOtherRef.current) {
            setRtmsErrorType(sessionData.statusMessage);
            setIsRtmsErrorModalOpen(true);
          }
        } else {
          // TODO: Re-enable checkForSummary when READY state UI is implemented
          /*
          setTimeout(async () => {
            const queryStartTime = rtmsStartTimestampRef.current - 10;
            const queryEndTime = rtmsStartTimestampRef.current + 60;
            const found = await checkForSummary({
              queryStartTime,
              queryEndTime,
              meetingUUID,
              summarizeStateRef,
              setAimsUuid,
              setSummaryData,
              setSummarizeState,
            });
            if (!found) {
              hideStoppedComponentTimerRef.current = setTimeout(() => {
                if (summarizeStateRef.current !== SUMMARIZE_STATES.SUMMARIZING) {
                  setSummarizeState(SUMMARIZE_STATES.DEFAULT);
                }
              }, 120000);
            }
          }, 60000);
          */
          
          hideStoppedComponentTimerRef.current = setTimeout(() => {
            if (summarizeStateRef.current !== SUMMARIZE_STATES.SUMMARIZING) {
              setSummarizeState(SUMMARIZE_STATES.DEFAULT);
            }
          }, 60000);
        }
      }).catch(error => {
        LogManager.error("Error fetching session status:", error);
      });
    }
  };
};

/**
 * Check for summary after RTMS has ended.
 * 
 * This function queries the backend to see if a summary is available
 * for the meeting after RTMS has stopped.
 * 
 * @param {object} params - Parameters for checking summary
 * @param {number} params.queryStartTime - Start time for query
 * @param {number} params.queryEndTime - End time for query
 * @param {string} params.meetingUUID - Meeting UUID
 * @param {object} params.summarizeStateRef - Ref for summarize state
 * @param {function} params.setAimsUuid - State setter for AIMS UUID
 * @param {function} params.setSummaryData - State setter for summary data
 * @param {function} params.setSummarizeState - State setter for summarize state
 * @returns {Promise<boolean>} Whether a summary was found
 */
export const checkForSummary = async (params) => {
  const {
    queryStartTime,
    queryEndTime,
    meetingUUID,
    summarizeStateRef,
    setAimsUuid,
    setSummaryData,
    setSummarizeState,
  } = params;

  try {
    const summaryListJson = await fetchSummaries(queryStartTime, queryEndTime, 1);
    if (summaryListJson?.summaries?.length > 0) {
      const summary = summaryListJson.summaries[0];
      if (summary.meetingUuid === meetingUUID && 
          summarizeStateRef.current !== SUMMARIZE_STATES.SUMMARIZING &&
          summarizeStateRef.current !== SUMMARIZE_STATES.DEFAULT) {
        setAimsUuid(summary.aimsUuid);
        setSummaryData(summary);
        setSummarizeState(SUMMARIZE_STATES.READY);
        return true;
      }
    }
    return false;
  } catch (error) {
    LogManager.error("Error checking for summary:", error);
    return false;
  }
};
